/**
 * NatureIdentify — /nature/identify
 * Two modes:
 *   • Photo — Claude Sonnet 4.5 vision (existing)
 *   • Audio — Gemini bird-song / animal-sound identification
 * Both write to the public citizen-science observations feed.
 */
import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Upload, ArrowLeft, Loader2, ShieldAlert, ExternalLink, Sparkles, X,
  Mic, Volume2, MapPin, Search,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LocationConfirmBar from "@/components/LocationConfirmBar";
import NatureSearchByName from "@/components/NatureSearchByName";

const PHOTO_HINTS = [
  { key: "auto",      label: "Auto-detect" },
  { key: "plant",     label: "Plant" },
  { key: "tree",      label: "Tree" },
  { key: "flower",    label: "Flower" },
  { key: "bird",      label: "Bird" },
  { key: "mammal",    label: "Animal" },
  { key: "fish",      label: "Fish" },
  { key: "marine",    label: "Marine" },
  { key: "reptile",   label: "Reptile" },
  { key: "amphibian", label: "Amphibian" },
  { key: "insect",    label: "Insect" },
  { key: "butterfly", label: "Butterfly" },
  { key: "mushroom",  label: "Mushroom" },
  { key: "spider",    label: "Spider" },
  { key: "rock",      label: "Rock" },
];

const AUDIO_HINTS = [
  { key: "bird",    label: "Bird" },
  { key: "frog",    label: "Frog / toad" },
  { key: "insect",  label: "Insect chorus" },
  { key: "mammal",  label: "Mammal" },
  { key: "whale",   label: "Whale / cetacean" },
  { key: "generic", label: "Auto-detect" },
];

async function _fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",", 2)[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function NatureIdentify() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode]     = useState("photo");   // "photo" | "audio"
  const [result, setResult] = useState(null);
  const [busy,  setBusy]    = useState(false);

  // Confirmed post-time location (from LocationConfirmBar). The parent
  // deliberately does NOT touch the global LocalityContext so the top
  // "You're viewing from…" bar keeps whatever the user set there.
  const [postLoc, setPostLoc] = useState(null);

  const requireLogin = () => {
    if (!user) { toast.error("Please sign in to use identification"); nav("/login?next=/nature/identify"); return false; }
    return true;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="nature-identify">
      <Link to="/nature" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Nature hub
      </Link>
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>AI IDENTIFICATION</div>
      <h1 className="font-display text-3xl md:text-4xl mb-1 inline-flex items-center gap-2">
        <Camera className="w-7 h-7" /> What is this?
      </h1>
      <p className="opacity-80 mb-4">
        Upload a photo of a plant, tree, flower, bird or animal — or record a
        bird song / frog call — and our AI will identify it for you. Free
        with your Social Pulse account.
      </p>

      {/* Mode switch */}
      <div className="inline-flex rounded-full border p-0.5 mb-6"
           style={{ borderColor: "var(--wofy-border)" }}
           data-testid="nature-identify-mode">
        <button type="button" onClick={() => { setMode("photo"); setResult(null); }}
                data-testid="nature-mode-photo"
                className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1 ${mode === "photo" ? "bg-black text-white" : "hover:bg-black/5"}`}>
          <Camera className="w-3 h-3" /> Photo ID
        </button>
        <button type="button" onClick={() => { setMode("audio"); setResult(null); }}
                data-testid="nature-mode-audio"
                className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1 ${mode === "audio" ? "bg-black text-white" : "hover:bg-black/5"}`}>
          <Mic className="w-3 h-3" /> Bird song
        </button>
        <button type="button" onClick={() => { setMode("name"); setResult(null); }}
                data-testid="nature-mode-name"
                className={`text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1 ${mode === "name" ? "bg-black text-white" : "hover:bg-black/5"}`}>
          <Search className="w-3 h-3" /> Search by name
        </button>
      </div>

      {mode === "photo" && (
        <PhotoIdentifier setResult={setResult} busy={busy} setBusy={setBusy}
                         postLoc={postLoc}
                         requireLogin={requireLogin} />
      )}
      {mode === "audio" && (
        <AudioIdentifier setResult={setResult} busy={busy} setBusy={setBusy}
                         postLoc={postLoc}
                         requireLogin={requireLogin} />
      )}
      {mode === "name" && (
        <NatureSearchByName />
      )}

      {/* Confirmable posting location — auto-detects, lets the user
          override (Country / State / City typeahead) without touching
          the global set-location bar. */}
      <LocationConfirmBar
        label="Where are you posting this from?"
        onConfirm={setPostLoc}
        testid="nature-post-loc"
      />

      {/* Result panel */}
      {result && <ResultPanel result={result} mode={mode} />}
    </div>
  );
}

/* ─── Photo identifier (existing logic, refactored) ─────────────── */
function PhotoIdentifier({ setResult, busy, setBusy, postLoc, requireLogin }) {
  const inputRef = useRef();
  const [subject, setSubject] = useState("auto");
  const [preview, setPreview] = useState(null);
  const [b64,     setB64]     = useState(null);

  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image is too large. Please use one under 6 MB."); return; }
    const r = new FileReader();
    r.onload = () => setPreview(String(r.result));
    r.readAsDataURL(file);
    setB64(await _fileToBase64(file));
    setResult(null);
  };

  const identify = async () => {
    if (!requireLogin()) return;
    if (!b64) { toast.error("Choose a photo first"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await http.post("/nature/atlas/identify", {
        image_base64: b64,
        subject_hint: subject,
        include_similar: true,
        lat: postLoc?.lat, lng: postLoc?.lng,
        city: postLoc?.city || undefined,
        country: postLoc?.country || undefined,
      });
      setResult(data);
      if (data?.observation_id) toast.success("Saved to community observations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Identification failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { setPreview(null); setB64(null); setResult(null); };

  return (
    <div className="wofy-card !p-6 mb-4">
      <div className="mb-4">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">What are you photographing?</div>
        <div className="flex flex-wrap gap-1.5" data-testid="nature-identify-hints">
          {PHOTO_HINTS.map(h => (
            <button key={h.key} type="button" onClick={() => setSubject(h.key)}
                    data-testid={`nature-hint-${h.key}`}
                    className={`text-xs px-3 py-1 rounded-full border ${subject === h.key ? "bg-black text-white" : "hover:bg-black/5"}`}
                    style={subject === h.key ? undefined : { borderColor: "var(--wofy-border)" }}>
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {!preview && (
        <label className="block border-2 border-dashed rounded-xl p-10 text-center cursor-pointer hover:bg-black/5 transition"
               style={{ borderColor: "var(--wofy-border)" }}
               data-testid="nature-identify-uploader">
          <Upload className="w-8 h-8 mx-auto mb-3 opacity-60" />
          <div className="font-medium">Click to upload a photo</div>
          <div className="text-xs opacity-70 mt-1">JPEG, PNG or WEBP — up to 6 MB</div>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
                 className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
        </label>
      )}
      {preview && (
        <div className="relative rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
          <img src={preview} alt="preview" className="w-full max-h-96 object-contain bg-black/5" />
          <button type="button" onClick={clear}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"
                  aria-label="Clear" data-testid="nature-identify-clear">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {preview && (
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={identify} disabled={busy}
                  className="wofy-btn-primary" data-testid="nature-identify-submit">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Identifying…</>)
                  : (<><Sparkles className="w-4 h-4" /> Identify</>)}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Audio (bird song) identifier ──────────────────────────────── */
function AudioIdentifier({ setResult, busy, setBusy, coords, locName, requireLogin }) {
  const [subject, setSubject] = useState("bird");
  const [audioFile,    setAudioFile]    = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);   // blob URL
  const [audioMime,    setAudioMime]    = useState(null);
  const [recording,    setRecording]    = useState(false);
  const mediaRecRef  = useRef(null);
  const chunksRef    = useRef([]);
  const streamRef    = useRef(null);

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Audio is too large. Please use one under 10 MB."); return; }
    setAudioFile(file);
    setAudioMime(file.type || "audio/mpeg");
    setAudioPreview(URL.createObjectURL(file));
    setResult(null);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const file = new File([blob], "recording.webm", { type: blob.type });
        pickFile(file);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error(e?.message || "Microphone access denied");
    }
  };

  const stopRecording = () => {
    try { mediaRecRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const clear = () => {
    setAudioFile(null); setAudioPreview(null); setAudioMime(null); setResult(null);
  };

  const identify = async () => {
    if (!requireLogin()) return;
    if (!audioFile) { toast.error("Choose or record an audio clip first"); return; }
    setBusy(true); setResult(null);
    try {
      const b64 = await _fileToBase64(audioFile);
      const { data } = await http.post("/nature/atlas/identify-audio", {
        audio_base64: b64,
        audio_mime:   audioMime || "audio/mpeg",
        subject_hint: subject,
        lat: coords?.lat, lng: coords?.lng,
        city: locName || undefined,
      });
      setResult(data);
      if (data?.observation_id) toast.success("Saved to community observations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audio identification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wofy-card !p-6 mb-4">
      <div className="mb-4">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">What are you recording?</div>
        <div className="flex flex-wrap gap-1.5" data-testid="nature-audio-hints">
          {AUDIO_HINTS.map(h => (
            <button key={h.key} type="button" onClick={() => setSubject(h.key)}
                    data-testid={`nature-audio-hint-${h.key}`}
                    className={`text-xs px-3 py-1 rounded-full border ${subject === h.key ? "bg-black text-white" : "hover:bg-black/5"}`}
                    style={subject === h.key ? undefined : { borderColor: "var(--wofy-border)" }}>
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {!audioPreview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-black/5 transition"
                 style={{ borderColor: "var(--wofy-border)" }}
                 data-testid="nature-audio-uploader">
            <Upload className="w-6 h-6 mx-auto mb-2 opacity-60" />
            <div className="font-medium text-sm">Upload audio clip</div>
            <div className="text-[11px] opacity-70 mt-1">MP3, WAV, M4A, OGG — up to 10 MB</div>
            <input type="file" accept="audio/*"
                   className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
          </label>
          <div className="border-2 border-dashed rounded-xl p-6 text-center"
               style={{ borderColor: "var(--wofy-border)" }}>
            <Mic className={`w-6 h-6 mx-auto mb-2 ${recording ? "animate-pulse text-red-600" : "opacity-60"}`} />
            <div className="font-medium text-sm">
              {recording ? "Recording…" : "Record from mic"}
            </div>
            <div className="text-[11px] opacity-70 mt-1 mb-2">
              10-30 seconds is usually best
            </div>
            {!recording ? (
              <button type="button" onClick={startRecording} className="wofy-btn-ghost text-xs"
                      data-testid="nature-audio-rec-start">
                Start recording
              </button>
            ) : (
              <button type="button" onClick={stopRecording} className="wofy-btn-primary text-xs"
                      data-testid="nature-audio-rec-stop">
                Stop
              </button>
            )}
          </div>
        </div>
      )}

      {audioPreview && (
        <div className="rounded-xl border p-3 relative" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2 inline-flex items-center gap-1">
            <Volume2 className="w-3 h-3" /> Preview
          </div>
          <audio controls src={audioPreview} className="w-full" data-testid="nature-audio-preview" />
          <button type="button" onClick={clear}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow"
                  aria-label="Clear" data-testid="nature-audio-clear">
            <X className="w-4 h-4" />
          </button>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={identify} disabled={busy}
                    className="wofy-btn-primary" data-testid="nature-audio-submit">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Identifying…</>)
                    : (<><Sparkles className="w-4 h-4" /> Identify</>)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared result panel ───────────────────────────────────────── */
function ResultPanel({ result, mode }) {
  const conf = result?.best_match?.confidence || "";
  const confColor = conf === "high"   ? "#16a34a"
                  : conf === "medium" ? "#d97706"
                  : conf === "low"    ? "#dc2626" : "#6b7280";
  return (
    <div className="wofy-card !p-6 mt-4" data-testid="nature-identify-result">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            {mode === "audio" ? "AUDIO MATCH" : "BEST MATCH"}
          </div>
          <h2 className="font-display text-3xl leading-tight">
            {result.best_match?.common_name || "Unknown"}
          </h2>
          {result.best_match?.scientific_name && (
            <div className="italic font-mono text-sm opacity-80 mt-0.5">
              {result.best_match.scientific_name}
            </div>
          )}
          {result.best_match?.family && (
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mt-0.5">
              Family · {result.best_match.family}
            </div>
          )}
        </div>
        {conf && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full"
                style={{ background: confColor, color: "white" }}
                data-testid="nature-identify-confidence">
            {conf} confidence
          </span>
        )}
      </div>
      {result.best_match?.reasoning && (
        <p className="text-sm opacity-85 mb-3">
          <span className="opacity-70">Why:</span> {result.best_match.reasoning}
        </p>
      )}
      {result.description && <p className="opacity-90 mb-3">{result.description}</p>}
      {result.habitat        && <p className="text-sm opacity-85 mb-1"><strong>Habitat:</strong> {result.habitat}</p>}
      {result.distribution   && <p className="text-sm opacity-85 mb-1"><strong>Distribution:</strong> {result.distribution}</p>}
      {result.conservation_status && (
        <p className="text-sm opacity-85 mb-1"><strong>Conservation:</strong> {result.conservation_status}</p>
      )}
      {result.safety_note && (
        <div className="rounded-lg p-3 mt-3 text-sm" style={{ background: "#FEF3C7", color: "#92400E" }}>
          <ShieldAlert className="w-4 h-4 inline mr-1" />
          {result.safety_note}
        </div>
      )}
      {(result.similar_species || []).length > 0 && (
        <section className="mt-4">
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>SIMILAR SPECIES</div>
          <ul className="space-y-1 text-sm">
            {result.similar_species.map((s, i) => (
              <li key={i}>
                <strong>{s.common_name}</strong>
                {s.scientific_name && <em className="opacity-70"> — {s.scientific_name}</em>}
                {(s.how_to_tell_apart || s.how_to_tell_apart_by_ear) &&
                  <span className="opacity-80"> · {s.how_to_tell_apart || s.how_to_tell_apart_by_ear}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {result.canonical_slug && (
          <Link to={`/nature/species/${result.canonical_slug}`}
                className="wofy-btn-primary text-sm"
                data-testid="nature-identify-full-profile">
            Full profile →
          </Link>
        )}
        {result.observation_id && (
          <Link to="/nature/observations" className="wofy-btn-ghost text-sm"
                data-testid="nature-identify-see-feed">
            See community feed
          </Link>
        )}
        {(result.further_reading || []).map((u, i) => {
          try {
            const host = new URL(u).host;
            return (
              <a key={i} href={u} target="_blank" rel="noopener noreferrer"
                 className="wofy-btn-ghost text-sm">
                <ExternalLink className="w-3 h-3" /> {host}
              </a>
            );
          } catch { return null; }
        })}
      </div>
    </div>
  );
}
